import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { getDialogueById } from '../data/dialogues';
import { getScoreRule } from '../data/scoreRules';
import { InteractionContext, Interactable } from '../interfaces/Interactable';
import { SaveSystem } from '../systems/SaveSystem';
import { AudioSystem } from '../systems/AudioSystem';
import { UIStyle } from '../ui/UIStyle';

export type CollectibleType = 'rafflesia' | 'keris_1' | 'keris_2' | 'keris_3';

const COLLECTIBLE_TEXTURE_MAP: Record<CollectibleType, string> = {
  rafflesia: TEXTURE_KEYS.RAFFLESIA,
  keris_1: TEXTURE_KEYS.KERIS_1,
  keris_2: TEXTURE_KEYS.KERIS_2,
  keris_3: TEXTURE_KEYS.KERIS_3,
};

export interface CollectibleConfig {
  id: string;
  label: string;
  type: CollectibleType;
  scoreRuleId?: string;
  dialogueId?: string;
  interactionRadius?: number;
  kerisId?: string;
  startHidden?: boolean;
  onCollect?: (ctx: InteractionContext) => void;
}

export class Collectible extends Phaser.Physics.Arcade.Sprite implements Interactable {
  readonly interactId: string;
  readonly collectibleId: string;
  readonly label: string;
  readonly collectibleType: CollectibleType;
  private scoreRuleId?: string;
  private dialogueId?: string;
  private kerisId?: string;
  private interactionRadius: number;
  private collected = false;
  private unlocked = true;
  private floatTween?: Phaser.Tweens.Tween;
  private glowTween?: Phaser.Tweens.Tween;
  private glowCircle?: Phaser.GameObjects.Arc;
  private onCollectCallback?: (ctx: InteractionContext) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    config: CollectibleConfig,
  ) {
    const texture = COLLECTIBLE_TEXTURE_MAP[config.type];
    super(scene, x, y, texture);

    this.interactId = config.id;
    this.collectibleId = config.id;
    this.label = config.label;
    this.collectibleType = config.type;
    this.scoreRuleId = config.scoreRuleId;
    this.dialogueId = config.dialogueId;
    this.kerisId = config.kerisId;
    this.interactionRadius = config.interactionRadius ?? 70;
    this.onCollectCallback = config.onCollect;
    this.collected = SaveSystem.isItemCollected(config.id);
    this.unlocked = config.startHidden ? false : true;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setDepth(7);
    const frame = scene.textures.getFrame(texture);
    this.body?.setSize(frame.width * 0.7, frame.height * 0.7);

    if (this.collected || !this.unlocked) {
      this.setVisible(false);
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.enable = false;
    }
  }

  setOnCollect(callback: (ctx: InteractionContext) => void): void {
    this.onCollectCallback = callback;
  }

  setUnlocked(unlocked: boolean): void {
    if (this.collected) return;
    this.unlocked = unlocked;
    if (unlocked) {
      this.setVisible(true);
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.enable = true;
      this.startAnimations();
      this.addGlow();
    } else {
      this.setVisible(false);
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.enable = false;
      this.stopAnimations();
    }
  }

  private startAnimations(): void {
    if (!this.floatTween) {
      this.floatTween = this.scene.tweens.add({
        targets: this,
        y: this.y - 6,
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private stopAnimations(): void {
    this.floatTween?.stop();
    this.glowTween?.stop();
    this.glowCircle?.destroy();
    this.glowCircle = undefined;
  }

  private addGlow(): void {
    this.glowCircle?.destroy();
    this.glowCircle = this.scene.add.circle(this.x, this.y, 34, 0xffd166, 0.25);
    this.glowCircle.setDepth(6);
    this.glowTween = this.scene.tweens.add({
      targets: this.glowCircle,
      scaleX: 1.2,
      scaleY: 1.2,
      alpha: 0.1,
      duration: 900,
      yoyo: true,
      repeat: -1,
    });
  }

  getInteractPosition(): Phaser.Math.Vector2 {
    return new Phaser.Math.Vector2(this.x, this.y);
  }

  getInteractionRadius(): number {
    return this.interactionRadius;
  }

  canInteract(): boolean {
    return this.unlocked && !this.collected && this.visible;
  }

  getPromptText(): string {
    return `Ambil ${this.label} — E`;
  }

  onInteract(ctx: InteractionContext): void {
    if (!this.canInteract()) return;
    this.collect(ctx);
  }

  collect(ctx: InteractionContext): boolean {
    if (this.collected) return false;
    this.collected = true;

    this.stopAnimations();
    this.playCollectEffect(ctx.scene);
    AudioSystem.playCollectSfx();

    if (this.scoreRuleId) {
      const rule = getScoreRule(this.scoreRuleId);
      if (rule) ctx.score.addScore(rule.points, rule.label);
    }

    if (this.kerisId) SaveSystem.collectKeris(this.kerisId);
    SaveSystem.markItemCollected(this.collectibleId);

    this.setVisible(false);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = false;

    if (this.dialogueId) {
      const dialogue = getDialogueById(this.dialogueId);
      if (dialogue) {
        ctx.dialogue.show(dialogue, () => this.onCollectCallback?.(ctx));
      } else {
        this.onCollectCallback?.(ctx);
      }
    } else {
      this.onCollectCallback?.(ctx);
    }

    return true;
  }

  updateGlow(): void {
    this.glowCircle?.setPosition(this.x, this.y);
  }

  private playCollectEffect(scene: Phaser.Scene): void {
    const burst = scene.add.circle(this.x, this.y, 8, 0xffd166, 0.9);
    burst.setDepth(20);
    scene.tweens.add({
      targets: burst,
      scaleX: 3,
      scaleY: 3,
      alpha: 0,
      duration: 400,
      onComplete: () => burst.destroy(),
    });

    const spark = scene.add.text(this.x, this.y - 20, '+', {
      ...UIStyle.onBrightBg(UIStyle.px(24), '#ffd166', true),
    });
    spark.setOrigin(0.5).setDepth(21);
    scene.tweens.add({
      targets: spark,
      y: this.y - 50,
      alpha: 0,
      duration: 600,
      onComplete: () => spark.destroy(),
    });
  }

  isCollected(): boolean {
    return this.collected;
  }
}
